import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { singletonHook } from 'react-singleton-hook';

import { IRiskSupplementWarningTypeResponseDTO } from '@interfaces/highRiskSupplements';
import { RiskSupplementWarningType } from '@models/riskSupplementWarningType';
import { handleGetHighRiskSupplementWarningTypes } from '@store/features/warningTypes/actions';
import {
  highRiskSupplementWarningTypesErrorSelector,
  highRiskSupplementWarningTypesIsLoadingSelector,
  highRiskSupplementWarningTypesIsReadySelector,
  highRiskSupplementWarningTypesSelector,
} from '@store/features/warningTypes/selectors';
import useDispatch from '@store/hooks/useDispatch';

import useFetchLookupData from './useFetchLookupData';

interface IWarningTypesHookData {
  data: IRiskSupplementWarningTypeResponseDTO[] | null;
  descriptionFragments: Map<string, string[]>;
}

const useWarningTypes = (): IWarningTypesHookData => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const data = useSelector(highRiskSupplementWarningTypesSelector);
  const isLoading = useSelector(highRiskSupplementWarningTypesIsLoadingSelector);
  const isReady = useSelector(highRiskSupplementWarningTypesIsReadySelector);
  const error = useSelector(highRiskSupplementWarningTypesErrorSelector);

  const descriptionFragments = useMemo(() => {
    const result = new Map();
    if (data) {
      data.forEach((item: RiskSupplementWarningType) =>
        result.set(item.normalizedName, item.descriptionFragments),
      );
    }
    return result;
  }, [data]);

  const handleFetch = useCallback(() => {
    dispatch(handleGetHighRiskSupplementWarningTypes());
  }, [dispatch]);

  useFetchLookupData({
    isReady,
    isLoading,
    error: error && t('errors.highRiskSupplementsWarningTypesError'),
    handleFetch,
  });

  return {
    data,
    descriptionFragments,
  };
};

export default singletonHook(
  {
    data: [] as RiskSupplementWarningType[],
    descriptionFragments: new Map(),
  },
  useWarningTypes,
);
